import { useGetCompanyMembersQuery } from "@/features/Members/api";
import {
  Avatar,
  Box,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Chip,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { Check, SearchOutlined, PersonAddAlt, MoreVert, Edit, Delete } from "@mui/icons-material";
import { colorPalette } from "../utils";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppSelector } from "@/app/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAddMembersToProjectMutation, useGetListOfMembersQuery, useUpdateMemberRoleMutation, useDeleteMemberFromProjectMutation } from "@/features/bughurd/api";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useStyles } from "@/Components/Common/styles";
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid-pro";
import { useCustomLocaleText } from "@/Components/Common/useCustomLocaleText";
import useDynamicHeight from "@/Components/scrollable/useDynamicHeight";
import Pagination from "@/Components/Common/TablePro/Pagination";
import { useSendInviteMutation } from "@/bug-tracker/app/features/Members/api";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";

export const MembersTab = ({ projectDetails, onProjectUpdate }) => {
  const { roleCodeDetails: myPermissions, currentUser } = useAppSelector(
    (state) => state.appGlobal
  );
  const { t } = useTranslation(["bugherd"]);
  const {
    data: companyMemberData,
    refetch,
  } = useGetCompanyMembersQuery(1);

  const theme = useTheme();
  const { id } = useParams();
  const classes = useStyles();

  // State for invite members popup
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [userRole, setUserRole] = useState("TEAM_MEMBER");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 10,
    page: 1,
  });

  // State for send invite feature
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const [sendInviteDialogOpen, setSendInviteDialogOpen] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });

  // State for actions menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const openActionsMenu = Boolean(menuAnchorEl);

  // State for edit role modal
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editRoleValue, setEditRoleValue] = useState("");

  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: projectMembersData, isLoading: isLoadingProjectMembers, isFetching: isFetchingProjectMembers } =
    useGetListOfMembersQuery({ projectId: id, page: paginationModel.page, size: paginationModel.pageSize, searchName: searchValue });

  // Get project members from API response
  const projectMembersList = projectMembersData?.data?.decryptedData?.items || [];
  const totalProjectMembers = projectMembersData?.data?.decryptedData?.total || 0;

  // Check if current user can manage members (owner, TEAM_LEAD, or MANAGER)
  const isProjectOwner = currentUser?._id === projectDetails?.owner;
  const currentUserMembership = projectMembersList.find(
    (member: any) => member?.user?._id === currentUser?._id
  );
  const isTeamLead = currentUserMembership?.userRole === "TEAM_LEAD";
  const isManager = currentUserMembership?.userRole === "MANAGER";
  const canManageMembers = isProjectOwner || isTeamLead || isManager;
  const [addMembersToProject, { isLoading: isAddingMember }] = useAddMembersToProjectMutation();
  const [sendInvite, { isLoading: isSendingInvite }] = useSendInviteMutation();
  const [updateMemberRole, { isLoading: isUpdatingRole }] = useUpdateMemberRoleMutation();
  const [deleteMemberFromProject, { isLoading: isDeletingMember }] = useDeleteMemberFromProjectMutation();

  // Reset pagination when search changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 1 }));
  }, [searchValue]);

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const nameParts = fullName?.split(" ");
    if (nameParts?.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    const initials = nameParts
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
    return initials?.length > 2 ? initials?.substring(0, 2) : initials;
  };

  const members = companyMemberData?.data?.decryptedData || [];

  // Extract user IDs from projectMembersList (API response) to filter out already added members
  const existingMemberIdsFromApi = projectMembersList?.map((item) => {
    // Handle both { user: { _id: ... } } and { _id: ... } structures
    return item?.user?._id || item?._id;
  }) || [];

  // Also get IDs from projectDetails.users to handle pagination (API might only return current page)
  const existingMemberIdsFromProject = (projectDetails?.users || []).map((user) => {
    if (typeof user === "object" && user?._id) {
      return user._id;
    }
    return user;
  });

  // Combine both sources to ensure all existing members are filtered out
  const allExistingMemberIds = Array.from(new Set([...existingMemberIdsFromApi, ...existingMemberIdsFromProject]));

  // Get available users to invite (exclude already assigned members and owner)
  const availableUsers = members?.filter(
    (member) =>
      !allExistingMemberIds?.includes(member._id) &&
      member._id !== projectDetails?.owner
  );

  const handleInviteClick = () => {
    setInviteDialogOpen(true);
  };

  const handleInviteCancel = () => {
    setInviteDialogOpen(false);
    setSelectedUsers([]);
    setUserRole("TEAM_MEMBER");
  };

  const handleInviteSubmit = async () => {
    try {
      const existingUsers = projectDetails?.users || [];
      const existingUserObjects = existingUsers.filter(
        (user) => typeof user === "object" && user?._id
      );

      // Convert selected users to the same format
      const newUserObjects = selectedUsers?.map((user) => ({
        _id: user._id,
        userId: user.userId,
        email: user.email,
        fullName: user.fullName,
      }));

      // Get existing user IDs to check for duplicates
      const existingUserIds = existingUserObjects.map((user) => user._id);

      // Filter out duplicates from new users
      const uniqueNewUsers = newUserObjects.filter(
        (user) => !existingUserIds.includes(user._id)
      );

      // Combine existing and new users
      // const updatedUsers = [...existingUserObjects, ...uniqueNewUsers];
      const updatedUsers = [...uniqueNewUsers];

      const payload = {
        items: updatedUsers?.map((user) => ({
          user: user._id,
          project: id,
          userRole: userRole,
          description: ''
        }))
      };
      const res = await addMembersToProject(payload);

      if (res?.data) {
        toast.success(t("Members added successfully"));
        setInviteDialogOpen(false);
        setSelectedUsers([]);
        setUserRole("TEAM_MEMBER");
        refetch();
      } else {
        toast.error(t("Failed to add users"));
      }
    } catch (error) {
      console.error("Error adding members:", error);
      toast.error(t("Failed to add users"));
    }
  };

  // Helper to check if input is a valid email format
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if no matching users found for the current input
  const filteredOptions = (availableUsers || []).filter(
    (user) =>
      user?.fullName?.toLowerCase().includes(autocompleteInputValue.toLowerCase()) ||
      user?.email?.toLowerCase().includes(autocompleteInputValue.toLowerCase())
  );
  const showSendInviteButton =
    autocompleteInputValue.trim() !== "" &&
    isValidEmail(autocompleteInputValue.trim()) &&
    filteredOptions.length === 0;

  const handleOpenSendInviteDialog = () => {
    setInviteFormData({
      email: autocompleteInputValue.trim(),
      firstName: "",
      lastName: "",
    });
    setSendInviteDialogOpen(true);
  };

  const handleCloseSendInviteDialog = () => {
    setSendInviteDialogOpen(false);
    setInviteFormData({
      email: "",
      firstName: "",
      lastName: "",
    });
  };

  const handleSendInviteSubmit = async () => {
    try {
      const payload = {
        items: [
          {
            email: inviteFormData.email,
            firstName: inviteFormData.firstName,
            lastName: inviteFormData.lastName,
            inviteType: "SNIFFER",
            inviteAs: userRole,
            snifferProject: id,
          },
        ],
      };
      const res: any = await sendInvite(payload);
      if (res?.data) {
        toast.success(t("Invitation sent successfully"));
        handleCloseSendInviteDialog();
        setAutocompleteInputValue("");
        handleInviteCancel()
        refetch();
      } else {
        const errorMessage =
          res?.error?.data?.error ||
          res?.error?.data?.message ||
          res?.error?.message ||
          t("Failed to send invitation");
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t("Failed to send invitation"));
    }
  };

  // Actions menu handlers
  const handleActionsMenuClick = (event: React.MouseEvent<HTMLElement>, member: any) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleActionsMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Edit role handlers
  const handleEditClick = () => {
    if (selectedMember) {
      setEditRoleValue(selectedMember.userRole || "TEAM_MEMBER");
      setEditRoleDialogOpen(true);
    }
    setMenuAnchorEl(null);
  };

  const handleEditRoleClose = () => {
    setEditRoleDialogOpen(false);
    setEditRoleValue("");
    setSelectedMember(null);
  };

  const handleEditRoleSubmit = async () => {
    if (!selectedMember) return;

    try {
      const res: any = await updateMemberRole({
        memberId: selectedMember._id,
        userRole: editRoleValue,
      });

      if (res?.data) {
        toast.success(t("Role updated successfully"));
        handleEditRoleClose();
        refetch();
      } else {
        const errorMessage =
          res?.error?.data?.message ||
          res?.error?.data?.error ||
          res?.error?.message ||
          t("Failed to update role");
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t("Failed to update role"));
    }
  };

  // Delete handlers
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedMember(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMember) return;

    try {
      const res: any = await deleteMemberFromProject({
        memberId: selectedMember._id,
      });

      if (res?.data || !res?.error) {
        toast.success(t("Member removed successfully"));
        setDeleteDialogOpen(false);
        setSelectedMember(null);
        refetch();
      } else {
        const errorMessage =
          res?.error?.data?.message ||
          res?.error?.data?.error ||
          res?.error?.message ||
          t("Failed to remove member");
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(t("Failed to remove member"));
    }
  };

  const columns: GridColDef[] = [
    {
      field: "fullName",
      headerName: t("Name"),
      headerClassName: classes.customTableHeader,
      flex: 1,
      renderCell: (params) => {
        // Check if this member is the project owner
        const isOwner = params.row?.user?._id === projectDetails?.owner || params.row?._id === projectDetails?.owner;
        const fullName = params.row?.user?.firstName + " " + params.row?.user?.lastName || "";

        return (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            className={classes.tableCellData}
          >
            <Avatar
              sx={{
                backgroundColor: isOwner
                  ? colorPalette[0]
                  : colorPalette[
                      params?.api?.getAllRowIds().indexOf(params.id) %
                        colorPalette.length
                    ],
                width: "25px",
                height: "25px",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {getInitials(fullName)}
            </Avatar>
            <Typography
              variant="body1"
              sx={{ fontWeight: isOwner ? 600 : 500 }}
            >
              {fullName}
            </Typography>
            {isOwner && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  px: 1,
                  py: 0.25,
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  borderRadius: 1,
                  fontSize: "10px",
                  fontWeight: 500,
                }}
              >
                {t("Owner")}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: t("Email"),
      headerClassName: classes.customTableHeader,
      flex: 1,
      renderCell: (params) => (
        <Typography className={classes.tableCellData}>
          {params.row?.user?.email || params.row?.email || ""}
        </Typography>
      ),
    },
    {
      field: "userRole",
      headerName: t("Invited As"),
      headerClassName: classes.customTableHeader,
      flex: 1,
      renderCell: (params) => (
        <Typography className={classes.tableCellData}>
          {params.value === 'CLIENT' ? t("Client") : params.value === 'MANAGER' ? t("Manager") : t("Member") || ""}
        </Typography>
      ),
    },
    // Only include actions column if user can manage members
    ...(canManageMembers
      ? [
          {
            field: "actions",
            headerName: "",
            headerClassName: classes.customTableHeader,
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: any) => {
              // Don't show actions for the project owner
              const isOwner =
                params.row?.user?._id === projectDetails?.owner ||
                params.row?._id === projectDetails?.owner;
              if (isOwner) return null;

              return (
                <Box className={classes.tableCellData}>
                  <IconButton
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLElement>) =>
                      handleActionsMenuClick(e, params.row)
                    }
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const GridCustomToolbar = (props: any) => {
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleClick = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };

    useEffect(() => {
      // Don't auto-focus if any dialog is open
      if (searchInputRef.current && !props.isDialogOpen) {
        searchInputRef.current.focus();
      }
    }, [props.searchValue, props.isDialogOpen]);

    return (
      <>
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          <Box sx={{ flexGrow: 1 }} />

          <TextField
            value={props.value}
            onChange={props.onChange}
            placeholder={t("Search...")}
            inputRef={searchInputRef}
            onClick={handleClick}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined />
                </InputAdornment>
              ),
            }}
            sx={{
              fieldset: { border: "none" },
            }}
            id="snr-ai-index-searchField"
          />
        </GridToolbarContainer>
      </>
    );
  };

  const requestSearch = useCallback(
    (val) => {
      setSearchValue(val);
    },
    [setSearchValue]
  );

  const localeText = useCustomLocaleText();
  const dynamicHeight = `calc(${useDynamicHeight()} - 100px)`;

  return (
    <>
      {/* Add User Button - Only visible to project owner or TEAM_LEAD */}
      {canManageMembers &&
        !DEFEKT_TRACKER.HIDE_CREATE(myPermissions) &&
        DEFEKT_TRACKER.CREATE(myPermissions) && (
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<PersonAddOutlinedIcon />}
              onClick={handleInviteClick}
              sx={{
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              {t("Add User")}
            </Button>
          </Box>
        )}
      <Box minHeight={"100px"}>
        <DataGridPro
          style={{
            height: `calc(${dynamicHeight} + 20px)`,
          }}
          getRowId={(row) => row?._id}
          columns={columns}
          rows={projectMembersList}
          disableColumnMenu
          pagination={true}
          loading={isLoadingProjectMembers || isFetchingProjectMembers}
          rowSelection={false}
          disableMultipleColumnsSorting
          localeText={localeText}
          hideFooter={false}
          slots={{
            toolbar: () => (
              <GridCustomToolbar
                value={searchValue}
                onChange={(e: any) => requestSearch(e.target.value)}
                isDialogOpen={inviteDialogOpen || sendInviteDialogOpen || editRoleDialogOpen || deleteDialogOpen}
              />
            ),
            pagination: () => (
              <Pagination
                total={totalProjectMembers}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
              />
            ),
            noRowsOverlay: () => (
              <GridOverlay>
                <div>{t("No Users Found")}</div>
              </GridOverlay>
            ),
            loadingOverlay: () => (
              <GridOverlay>
                <CircularProgress />
              </GridOverlay>
            ),
          }}
        />
      </Box>
      {/* Invite Members Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleInviteCancel} fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {t("Add Users to Project")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="demo-simple-select-label">{t("Invite As")}</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              label={t("Invite As")}
            >
              <MenuItem value="TEAM_MEMBER">{t("Member")}</MenuItem>
              <MenuItem value="CLIENT">{t("Client")}</MenuItem>
              <MenuItem value="MANAGER">{t("Manager")}</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={availableUsers}
            getOptionLabel={(option) => option?.fullName || ""}
            disableCloseOnSelect
            value={selectedUsers}
            inputValue={autocompleteInputValue}
            onInputChange={(_, newInputValue) => {
              setAutocompleteInputValue(newInputValue);
            }}
            onChange={(_, newValue) => {
              setSelectedUsers(newValue);
            }}
            filterOptions={(options, state) => {
              const filtered = options.filter(
                (option) =>
                  option?.fullName?.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                  option?.email?.toLowerCase().includes(state.inputValue.toLowerCase())
              );
              return filtered;
            }}
            noOptionsText={
              showSendInviteButton ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("No user found with this email")}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddAlt />}
                    onClick={handleOpenSendInviteDialog}
                  >
                    {t("Send Invite")}
                  </Button>
                </Box>
              ) : (
                t("No options")
              )
            }
            renderTags={(tagValues, getTagProps) =>
              tagValues?.map((option, index) => {
                if (!option?._id || !option?.fullName) {
                  console.error("Invalid user data", option);
                  return null;
                }

                return (
                  <Chip
                    key={option?._id || index}
                    label={option?.fullName}
                    {...getTagProps({ index })}
                    onDelete={() => {
                      const updatedUsers = selectedUsers.filter(
                        (user) => user._id !== option._id
                      );
                      setSelectedUsers(updatedUsers);
                    }}
                  />
                );
              })
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      mr: 2,
                      backgroundColor: colorPalette[0],
                      fontSize: "12px",
                    }}
                  >
                    {getInitials(option?.fullName)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">{option?.fullName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option?.email}
                    </Typography>
                  </Box>
                  {selected && (
                    <Check
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        ml: 1,
                      }}
                    />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("Search and select users...")}
                fullWidth
              />
            )}
          />
          {availableUsers?.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 2 }}
            >
              {t("All company users are already part of this project")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleInviteCancel} variant="outlined">
            {t("Cancel")}
          </Button>
          <LoadingButton
            onClick={handleInviteSubmit}
            variant="contained"
            disabled={selectedUsers?.length === 0}
            loading={isAddingMember}
          >
            {t("Add Selected Users")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Send Invite Dialog */}
      <Dialog open={sendInviteDialogOpen} onClose={handleCloseSendInviteDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {t("Send Invitation")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label={t("Email")}
              value={inviteFormData.email}
              onChange={(e) =>
                setInviteFormData({ ...inviteFormData, email: e.target.value })
              }
              fullWidth
              required
              type="email"
            />
            <TextField
              label={t("First Name")}
              value={inviteFormData.firstName}
              onChange={(e) =>
                setInviteFormData({ ...inviteFormData, firstName: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label={t("Last Name")}
              value={inviteFormData.lastName}
              onChange={(e) =>
                setInviteFormData({ ...inviteFormData, lastName: e.target.value })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseSendInviteDialog} variant="outlined">
            {t("Cancel")}
          </Button>
          <LoadingButton
            onClick={handleSendInviteSubmit}
            variant="contained"
            disabled={!inviteFormData.email || !inviteFormData.firstName}
            loading={isSendingInvite}
          >
            {t("Send Invite")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={openActionsMenu}
        onClose={handleActionsMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          {t("Edit Role")}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          {t("Remove")}
        </MenuItem>
      </Menu>

      {/* Edit Role Dialog */}
      <Dialog open={editRoleDialogOpen} onClose={handleEditRoleClose} fullWidth maxWidth="xs">
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            {t("Edit Member Role")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="edit-role-label">{t("Role")}</InputLabel>
            <Select
              labelId="edit-role-label"
              value={editRoleValue}
              onChange={(e) => setEditRoleValue(e.target.value)}
              label={t("Role")}
            >
              <MenuItem value="TEAM_MEMBER">{t("Member")}</MenuItem>
              <MenuItem value="CLIENT">{t("Client")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleEditRoleClose} variant="outlined">
            {t("Cancel")}
          </Button>
          <LoadingButton
            onClick={handleEditRoleSubmit}
            variant="contained"
            loading={isUpdatingRole}
          >
            {t("Save")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        loading={isDeletingMember}
        title={t("Remove Member")}
        confirmMessage={t("Are you sure you want to remove this member from the project?")}
        okText={t("Remove")}
      />
    </>
  );
};
